import React, { useMemo } from "react";
import { Box, Chip, Button } from "@mui/material";
import { useIntl, FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import translations from "app/libs/i18n/translations.json";
import { experience, degrees } from "bundles/common/utils/constants";

export const cvThequePillSx = {
  height: 28,
  borderRadius: "9999px",
  border: "1px solid hsla(153, 68%, 25%, 0.25)",
  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
  fontWeight: 500,
  backgroundColor: "hsla(153, 68%, 25%, 0.1)",
  color: "hsl(153, 68%, 25%)",
  "& .MuiChip-deleteIcon": {
    color: "hsl(153, 68%, 25%)",
    opacity: 0.6,
    "&:hover": { color: "hsl(153, 68%, 25%)", opacity: 1 },
  },
  "&:hover": { backgroundColor: "hsla(153, 68%, 25%, 0.18)" },
};

const LANGUAGE_LABEL_IDS = {
  fr: "language.french",
  nl: "language.dutch",
  en: "language.english",
};

const LAST_ACTIVE_LABEL_IDS = {
  7: "recruiter.cv_theque.last_active_7",
  30: "recruiter.cv_theque.last_active_30",
  90: "recruiter.cv_theque.last_active_90",
};

const rangesEqual = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};

const CVThequeActiveFilters = ({
  selectedLocations = [],
  setSelectedLocations,
  selectedRadiusKm = 0,
  setSelectedRadiusKm,
  selectedLanguages = [],
  setSelectedLanguages,
  selectedSectors = [],
  setSelectedSectors,
  selectedAvailabilities = [],
  setSelectedAvailabilities,
  selectedExperiences = [],
  setSelectedExperiences,
  selectedHardSkills = [],
  setSelectedHardSkills,
  selectedSoftSkills = [],
  setSelectedSoftSkills,
  selectedHighestDegrees = [],
  setSelectedHighestDegrees,
  selectedCandidateStatuses = [],
  setSelectedCandidateStatuses,
  selectedActiveWithin = null,
  setSelectedActiveWithin,
  booleanClauses = [],
  setBooleanClauses,
  onlyFavorites = false,
  setOnlyFavorites,
  onClearAll,
}) => {
  const intl = useIntl();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const chips = useMemo(() => {
    const items = [];

    selectedLocations.forEach((location) => {
      const name = location.type === "town" ? (location.label || "").split(",")[0].trim() : location.label;
      items.push({
        key: `location-${location.code}`,
        label: location.type !== "country" && selectedRadiusKm > 0 ? `${name} + ${selectedRadiusKm} km` : name,
        onDelete: () => {
          const next = selectedLocations.filter((l) => l.code !== location.code);
          setSelectedLocations(next);
          if (next.length === 0) setSelectedRadiusKm(0);
        },
      });
    });

    if (onlyFavorites) {
      items.push({
        key: "only-favorites",
        label: intl.formatMessage({
          id: "nav.recruiter.favorite_candidates",
          defaultMessage: "Saved candidates",
        }),
        onDelete: () => setOnlyFavorites(false),
      });
    }

    selectedLanguages.forEach((code) => {
      const labelId = LANGUAGE_LABEL_IDS[code];
      items.push({
        key: `lang-${code}`,
        label: labelId ? intl.formatMessage({ id: labelId, defaultMessage: code }) : code,
        onDelete: () => setSelectedLanguages(selectedLanguages.filter((c) => c !== code)),
      });
    });

    selectedSectors.forEach((sector) => {
      items.push({
        key: `sector-${sector}`,
        label: intl.formatMessage({ id: `company.company_industry.${sector}`, defaultMessage: sector }),
        onDelete: () => setSelectedSectors(selectedSectors.filter((s) => s !== sector)),
      });
    });

    selectedAvailabilities.forEach((availability) => {
      items.push({
        key: `availability-${availability}`,
        label: intl.formatMessage({ id: `candidate.${availability}`, defaultMessage: availability }),
        onDelete: () =>
          setSelectedAvailabilities(selectedAvailabilities.filter((a) => a !== availability)),
      });
    });

    selectedExperiences.forEach((range, idx) => {
      const xp = experience.find((option) => rangesEqual(option.value, range));
      const label = xp
        ? `${xp.label} ${intl.formatMessage({ id: "candidate.years", defaultMessage: "years" })}`
        : intl.formatMessage({ id: "recruiter.cv_theque.years_of_experience", defaultMessage: "Years of experience" });
      items.push({
        key: `xp-${idx}-${xp?.id ?? "x"}`,
        label,
        onDelete: () =>
          setSelectedExperiences(selectedExperiences.filter((r) => !rangesEqual(r, range))),
      });
    });

    selectedHardSkills.forEach((skill) => {
      items.push({
        key: `hard-${skill}`,
        label: translations[i18nLocale]?.[`skills_list.hard.${skill}`] || skill,
        onDelete: () => setSelectedHardSkills(selectedHardSkills.filter((s) => s !== skill)),
      });
    });

    selectedSoftSkills.forEach((skill) => {
      items.push({
        key: `soft-${skill}`,
        label: translations[i18nLocale]?.[`skills_list.soft.${skill}`] || skill,
        onDelete: () => setSelectedSoftSkills(selectedSoftSkills.filter((s) => s !== skill)),
      });
    });

    selectedHighestDegrees.forEach((code) => {
      const degree = degrees.find((d) => d.code === code);
      items.push({
        key: `degree-${code}`,
        label: degree ? intl.formatMessage({ id: degree.label, defaultMessage: code }) : code,
        onDelete: () => setSelectedHighestDegrees(selectedHighestDegrees.filter((c) => c !== code)),
      });
    });

    selectedCandidateStatuses.forEach((status) => {
      items.push({
        key: `candidate-status-${status}`,
        label: intl.formatMessage({ id: `candidate.status_map.${status}`, defaultMessage: status }),
        onDelete: () =>
          setSelectedCandidateStatuses(selectedCandidateStatuses.filter((s) => s !== status)),
      });
    });

    if (selectedActiveWithin) {
      const labelId = LAST_ACTIVE_LABEL_IDS[selectedActiveWithin];
      items.push({
        key: `active-within-${selectedActiveWithin}`,
        label: labelId
          ? intl.formatMessage({ id: labelId, defaultMessage: `${selectedActiveWithin} days` })
          : `${selectedActiveWithin}`,
        onDelete: () => setSelectedActiveWithin(null),
      });
    }

    (booleanClauses || []).forEach((clause, idx) => {
      const term = (clause?.term || "").toString().trim();
      if (!term) return;
      const fieldLabel = intl.formatMessage({
        id: `recruiter.cv_theque.boolean_field.${clause.field}`,
        defaultMessage: clause.field,
      });
      items.push({
        key: `boolean-${idx}`,
        label: `${fieldLabel}: ${term}`,
        onDelete: () => setBooleanClauses((booleanClauses || []).filter((_, i) => i !== idx)),
      });
    });

    return items;
  }, [
    intl,
    i18nLocale,
    selectedLocations,
    setSelectedLocations,
    selectedRadiusKm,
    setSelectedRadiusKm,
    onlyFavorites,
    setOnlyFavorites,
    selectedLanguages,
    setSelectedLanguages,
    selectedSectors,
    setSelectedSectors,
    selectedAvailabilities,
    setSelectedAvailabilities,
    selectedExperiences,
    setSelectedExperiences,
    selectedHardSkills,
    setSelectedHardSkills,
    selectedSoftSkills,
    setSelectedSoftSkills,
    selectedHighestDegrees,
    setSelectedHighestDegrees,
    selectedCandidateStatuses,
    setSelectedCandidateStatuses,
    selectedActiveWithin,
    setSelectedActiveWithin,
    booleanClauses,
    setBooleanClauses,
  ]);

  if (chips.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        mb: 2,
      }}
    >
      {chips.map((chip) => (
        <Chip key={chip.key} label={chip.label} onDelete={chip.onDelete} sx={cvThequePillSx} />
      ))}
      <Button
        onClick={onClearAll}
        sx={{
          textTransform: "none",
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
          fontWeight: 500,
          border: "none",
          backgroundColor: "transparent",
          color: "hsl(178, 10%, 45%)",
          "&:hover": { backgroundColor: "transparent", color: "hsl(0, 84.2%, 60.2%)" },
        }}
      >
        <FormattedMessage id="recruiter.cv_theque.clear_all_filters" defaultMessage="Clear all" />
      </Button>
    </Box>
  );
};

export default CVThequeActiveFilters;
